import { Scatter } from "react-chartjs-2";

import {
  darkGreyCol,
  greyCol,
  highD,
  lowB,
  mediumC,
  midGreyCol,
  veryHighE,
  veryLowA,
} from "../colors";
import { createYAxisLabelBackgroundPlugin } from "../plugins/createYAxisLabelBackgroundPlugin";
import { externalTooltipHandler } from "../utils/TooltipUtils";

export default function ScatterChart({
  data,
  thresholds,
  customTooltip = null,
  xAxisTtile,
  yAxisTtile,
  handleClick,
}: {
  data: any;
  thresholds: any;
  customTooltip?: ((tooltip: { dataPoints: any[] }) => React.ReactNode) | null;
  xAxisTtile?: string;
  yAxisTtile?: string;
  handleClick?: (element: any) => void;
}) {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (event: any, chartElements: any[]) => {
      const target = event.native?.target || event.target;
      if (chartElements.length) {
        target.style.cursor = "pointer";
      } else {
        target.style.cursor = "default";
      }
    },
    onClick: (event: any, elements: any[], chart: any) => {
      if (elements.length === 0) return;

      const clickedElement = elements[0];
      const datasetIndex = clickedElement.datasetIndex;
      const index = clickedElement.index;

      const point = chart.data.datasets[datasetIndex].data[index];
      handleClick?.(point);
    },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      title: {
        display: false,
      },
      ...(customTooltip !== null
        ? {
            tooltip: {
              enabled: false,
              external: (context: { tooltip: any; chart: any }) => {
                const dataset = context?.tooltip?.dataPoints?.[0]
                  ?.dataset as any;

                if (!dataset || dataset?.temp) return;

                if (!context.tooltip || !context.chart) return;

                return externalTooltipHandler(
                  context,
                  false,
                  customTooltip as any,
                  undefined,
                  300
                );
              },
            },
          }
        : {}),
    },
    scales: {
      x: {
        title: {
          display: true,
          text: xAxisTtile,
          color: darkGreyCol,
          font: {
            size: 12,
            weight: 400,
          },
        },
        min: 0,
        ticks: {
          align: "end",
          color: midGreyCol,
          font: {
            size: 12,
            weight: 400,
          },
        },
        grid: {
          display: true,
          drawTicks: false,
          drawOnChartArea: true,
          color: "#F2F3F5",
        },
      },
      y: {
        max: thresholds[thresholds.length - 1]?.upperBound,
        afterBuildTicks: (axis: any) => {
          if (!thresholds?.length) return;

          const tickValues = thresholds.map((t: any) => t.lowerBound);
          tickValues.push(thresholds[thresholds.length - 1]?.upperBound);

          axis.ticks = [...new Set([0, ...tickValues])]
            .sort((a, b) => a - b)
            .map((value) => ({ value }));
        },

        beginAtZero: true,

        title: {
          padding: {
            bottom: 4,
          },
          display: true,
          color: darkGreyCol,
          text: yAxisTtile,
          font: {
            size: 12,
            weight: 400,
          },
        },
        min: 0,
        ticks: {
          padding: 12,
          autoSkip: false,
          stepSize: undefined,
          maxTicksLimit: 12,
          borderDash: [8, 4],
          font: {
            size: 13,
            weight: 400,
          },

          color: (context: any) => {
            if (!thresholds?.length) return midGreyCol;
            const value = context.tick.value;

            const match = thresholds.find((t: any) => t.lowerBound === value);
            return match ? "transparent" : midGreyCol;
          },

          callback(value: any) {
            if (!thresholds?.length) return null;
            return thresholds.some((t: any) => t.lowerBound === value) ||
              value === 0 ||
              value === thresholds[thresholds.length - 1]?.upperBound
              ? value.toFixed(2)
              : "";
          },
        },
        dash: [6, 6],
        grid: {
          drawTicks: false,
          drawBoder: true,
          color: (context: any) => {
            const value = context.tick.value;
            const match = thresholds.find((t: any) => t.lowerBound === value);
            return match ? match.color : greyCol;
          },

          dash: [6, 6],
        },
        border: { dash: [6, 6], display: false },
      },
    },
  };
  const labelColors = [veryLowA, lowB, mediumC, highD, veryHighE];

  return (
    <div style={{ width: "100%", minHeight: 350 }}>
      <Scatter
        data={data as any}
        options={options as any}
        plugins={[createYAxisLabelBackgroundPlugin({ labelColors })]}
      />
    </div>
  );
}
